// ── TC5 — the Audit Center ───────────────────────────────────────────────────────────────────
//
// ⚠ THE GRANT IS A RESOLVED, FROZEN CONTROL SET. A framework is how a scope gets BUILT, not what
// it means — so readiness here is derived over the engagement's own controls, and the framework a
// control came from is PROVENANCE, shown to explain a grant and never used to decide one.

/** Readiness over an engagement's control set. Counts only — no percentage is ever stored. */
export interface ScopeReadiness {
  total: number;
  implemented: number;
  partially_implemented: number;
  not_implemented: number;
  unknown: number;
  /** ⚠ Its own bucket. "Nobody has assessed this" is not an assessment outcome. */
  not_assessed: number;
  excluded_na: number;
}

/** ⚠ PROVENANCE ONLY — how the scope was built. Never what a read filters on. */
export interface EngagementProvenance {
  source_framework_reference: string | null;
  name: string | null;
  control_count: number;
}

export interface Engagement {
  token_id: string;
  auditor_display_name: string;
  audit_firm_name: string;
  status: string;
  expires_at: string;
  window_state: 'active' | 'expired' | 'revoked' | 'pending_activation';
  control_count: number;
  resolved_at: string | null;
  /** ⚠ null when the scope is empty — never a zeroed bucket set. */
  readiness: ScopeReadiness | null;
  provenance: EngagementProvenance[];
  requests_total: number;
  requests_fulfilled: number;
  requests_open: number;
  /** ⚠ Derived from (due_at, status, now()) — nothing stores it, so it cannot go stale. */
  requests_overdue: number;
}

export interface AuditOverview {
  engagements: Engagement[];
  /** Live engagements whose scope resolved to nothing — surfaced, not swallowed. */
  empty_scope_count: number;
  /** Tenant-wide, deduplicated across engagements: two auditors sharing SOC 2 is one SOC 2. */
  readiness: FrameworkReadiness[];
}

/** One row of the scope builder's review screen. */
export interface ScopePreviewRow {
  framework_reference: string;
  display_code: string;
  name: string;
  /**
   * ⚠ The framework's OWN structural level (`ccl_framework_nodes`) — NOT VeriGRC's Domain → Family
   * taxonomy over controls. They are different hierarchies, and `group_label` carries the
   * framework's own word for its level so the screen never calls both of them "family".
   */
  group_count: number;
  group_label: string | null;
  requirement_count: number;
  /** DISTINCT controls — one control satisfying four requirements is ONE permission. */
  control_count: number;
}

/**
 * Requirement-grain readiness for one framework — TC3's fold, relabelled for an audit reader.
 *
 * ⚠ `unknown` is its own count. Somebody assessed the control and recorded that they could not
 * tell; folding it into "not started" would erase an answer that was actually given.
 */
export interface FrameworkReadiness {
  framework_reference: string;
  display_code: string;
  name: string;
  /** The framework's OWN word for its top level ("Trust Service Criteria"), read from its data. */
  hierarchy_label: string | null;
  met: number;
  partially: number;
  not_met: number;
  not_started: number;
  unknown: number;
  unmapped: number;
  /**
   * ⚠ Mapped in the library, but by no control in THIS grant — a scope gap, not a content gap.
   * Kept apart from `unmapped` because the remedies differ: widen the grant vs. author a mapping.
   * Always 0 on the programme-wide reading, where nothing is out of scope by definition.
   */
  out_of_scope: number;
  not_applicable: number;
  /** met + partially + not_met — the requirements anyone has actually been able to judge. */
  assessed: number;
  total: number;
}

/** One node of the framework's own hierarchy, with the same classification rolled up. */
export interface FrameworkNode {
  framework_reference: string;
  node_code: string;
  node_name: string;
  met: number;
  assessable: number;
  total: number;
  unmapped_codes: string[];
  out_of_scope_codes: string[];
  na_codes: string[];
}

/** One control inside an engagement, with what it answers for. */
export interface EngagementControl {
  control_reference: string;
  display_code: string | null;
  title: string | null;
  source_framework_reference: string | null;
  result: string | null;
  applicability: string | null;
  /**
   * ⚠ P1.4: these count artifacts WITHIN vs PAST the catalogue's recommended cadence — not a validity
   * window, which the go-forward model does not have. Nothing renders them today; whatever does must
   * use those words ("within/past recommended cadence"), never "fresh/stale" or "expired".
   */
  evidence_fresh: number;
  evidence_stale: number;
  open_requests: number;
  /** ⚠ Read LIVE: the scope is frozen, the explanation of it is not. */
  satisfies: string[];
}

export interface EngagementDetail {
  engagement: Engagement;
  controls: EngagementControl[];
  readiness: FrameworkReadiness[];
  nodes: FrameworkNode[];
  requests: EvidenceRequest[];
}

export interface EvidenceRequest {
  id: string;
  control_state_id: string;
  control_reference: string;
  control_code: string | null;
  control_title: string | null;
  token_id: string | null;
  audit_firm_name: string | null;
  title: string;
  detail: string | null;
  status: 'open' | 'fulfilled' | 'withdrawn';
  due_at: string | null;
  due_state: 'none' | 'upcoming' | 'overdue' | 'closed';
  raised_at: string;
  raised_by_name: string | null;
  satisfied_by_evidence_id: string | null;
  satisfied_by_title: string | null;
  fulfilled_at: string | null;
  withdrawn_at: string | null;
  withdrawn_reason: string | null;
}

/**
 * ⚠ THE ONE PLACE A READINESS PERCENTAGE IS COMPUTED, AND IT RETURNS NULL RATHER THAN ZERO.
 *
 * TC3 established this as a RETURN TYPE, not a formatting rule: a scope with nothing scorable has
 * no percentage, and `0` would be a measurement nobody took. The legacy `pol_compliance_readiness`
 * stores `readiness_pct DEFAULT 0` and cannot tell those apart — which is the whole reason the new
 * model derives instead.
 */
export function readinessPercent(r: ScopeReadiness | null): number | null {
  if (!r) return null;
  // Excluded controls leave the denominator: excluding one removes work from the programme, it
  // does not count as work not done.
  const scorable = r.total - r.excluded_na;
  if (scorable <= 0) return null;
  // ⚠ `not_assessed` STAYS in the denominator. Unmeasured work is not finished work, and dropping
  // it would let a programme nobody has looked at report 100%.
  // No partial credit: a bucket, never a fraction.
  return Math.round((r.implemented / scorable) * 100);
}

/** The label for the framework's own structural level, or a neutral fallback. */
export function groupLabel(row: ScopePreviewRow): string {
  const base = row.group_label ?? 'group';
  return `${row.group_count} ${base}${row.group_count === 1 ? '' : 's'}`;
}

/**
 * ⚠ THE FRAMEWORK-GRAIN PERCENTAGE, and the ONLY one at this grain.
 *
 * `met / total` — every requirement in the denominator, including the ones nobody can assess yet.
 * That is deliberately the *conservative* reading: a framework with 14 met out of 29 is 48%, not
 * the 61% you would get by quietly dropping the 6 that are unmapped, N/A or never started.
 * ⓘ Which is why the detail view shows **no percentage at all** and lists the counts instead —
 * one number cannot carry "23 of 29 were assessable" without saying so.
 */
export function frameworkPercent(r: FrameworkReadiness): number | null {
  if (r.total <= 0) return null;
  return Math.round((r.met / r.total) * 100);
}

/** Requirements nobody can judge yet. Never counted as failing — they are gaps, not failures. */
export function notYetAssessable(r: FrameworkReadiness): number {
  return r.not_started + r.unknown + r.unmapped + r.out_of_scope + r.not_applicable;
}
